/*!
 * doTERRA Web-UI
 * http://www.doterra.com
 *
 * Copyright 2019 doTERRA Holdings, LLC
 * All Rights Reserved
 */
import React, { useContext, useEffect, useReducer } from 'react';
import { AddPaymentList, PaymentList, AddExpressPaymentList } from "../components/payments";
import { usePaymentsState, usePaymentsMutator } from '../contexts/payments';
import { CreditCardPanel } from './credit-card-panel';
import { StandardButton } from "../components/controls";
import { text } from "../utils/i18n";
import { AddDigitalWalletPanel } from "./add-digitalWallet-panel";
import { EditDigitalWalletPanel } from "./edit-digital-wallet-panel";

const SET_VIEW_MODE = 'SET_VIEW_MODE';

const PaymentViewStateContext = React.createContext();
const PaymentViewDispatchContext = React.createContext();


const getViewOptions = (context) => {
    const options = {
        selectable: false, // items in the payment list are selectable
        fullWidth: false, // view is full-width or half-width (NOTE: full/half relative to parent div)
        backButton: true, // show/allow the back button
        makeDefault: true, // show the 'Make default...' button
        showAddButton: true, // determines if the add payment method button shows; enrollment flow does not use it
        useSavedAddress: true, // show the 'Use a saved address' button
        listCards: true, // when in list mode, show only vaulted cards (credit/debit)
        listOther: true, // when in list mode, show only vaulted digital wallets
        listAll: false, // when in list mode, show all vaulted payment types
        submitId: 'paymentAddSubmit'
    };
    if (context === 'modal' || context === 'subscriptionSettings' || context === 'crm-subscription') {
        options.selectable = true;
        options.fullWidth = true;
        options.listCards = false;
        options.listOther = false;
        options.listAll = true;
    } else if (context === 'enrollment') {
        options.backButton = false;
        options.fullWidth = true;
        options.useSavedAddress = false;
        options.makeDefault = false;
        options.showAddButton = false;
        options.submitId = 'enrollment-pay-submit';
    } else if (context === 'crm') {
        options.fullWidth = true;
    } else if (context === 'express-checkout') {
        options.fullWidth = true;
        options.backButton = false;
        options.useSavedAddress = false;
        options.makeDefault = false;
        options.showAddButton = false;
    }

    return options;
};


const PaymentViewReducer = (state, action) => {
    switch (action.type) {
        case SET_VIEW_MODE: {
            const setViewMode = action.payload;
            return {...state, mode: setViewMode};
        }
        default: {
            throw new Error(`[PaymentViewReducer] Unsupported action type: ${action.type}.`);
        }
    }
};



export const PaymentView = (props) => {
    const { markets, initialMode, showList = false, citMitMessage, closeModal, modelName, brainTreeToken, onChange, onSubmit,
      paymentLineID, digitalWalletEnv, googlePayMerchantId,recaptchaKey, disableContinue,
      isCRM, subscriptionCode, csrfToken, setSubscriptionPayment,
      cartShippingAddress } = props;
    const { context, methods, payments } = usePaymentsState();
    let mode = initialMode || 'list';
    if (mode === 'select' && methods.length === 1 && showList === false) {
        mode = isCRM ? 'crmaddCard' : 'addCard';
    }
    
    const [state, dispatch] = useReducer(PaymentViewReducer, {mode, viewOptions: getViewOptions(context), methodCount: methods.length});
    const { selectCartPayment, selectSubscriptionCardPayment } = usePaymentsMutator();

    const showSelectMethodList = () => {
        window.scrollTo(0, 0);
        dispatch({type: SET_VIEW_MODE, payload: methods.length === 1 ? 'addCard' : 'select'});
    };
    const showAddPayment = (method) => {
        window.scrollTo(0, 0);
        if (method.method === 'card') {
            method.method = isCRM ? 'crmaddCard' : 'addCard';
        }
        const payload = method.method;
        dispatch({type: SET_VIEW_MODE, payload});
    };
    const paymentClicked = (id) => {
        if ((context === 'modal' || context === 'subscriptionSettings' || context === 'crm-subscription') && mode === 'list') {
          if(setSubscriptionPayment) {
            selectSubscriptionCardPayment(id, isCRM, subscriptionCode, csrfToken, setSubscriptionPayment);
          } else {
            selectCartPayment(id);
          }

          closeModal(modelName);
        }
    };
    useEffect(() => {
        dispatch({type: SET_VIEW_MODE, payload: mode});
    }, [methods,initialMode]);

    let runEnv = 'sandbox';
    if (digitalWalletEnv)
        {runEnv = digitalWalletEnv;}

    let googlePayMerchant = '';
    if (googlePayMerchantId)
        {googlePayMerchant = googlePayMerchantId;}

    if(payments.length === 0 && state.mode === 'list') {
        if(disableContinue) {
            disableContinue(true);
        } 
    } else if (payments.length === 0 && state.mode === 'addCard') {
        if(disableContinue) {
            disableContinue(false);
        } 
    }

    return (
        <PaymentViewStateContext.Provider value={state}>
            <PaymentViewDispatchContext.Provider value={dispatch}>
                {state.mode === 'list' && <>
                    {state.viewOptions.listCards &&
                        <PaymentList type='cards' title={text("form.paymentManagement.creditCardHeader")} clickHandler={paymentClicked} isCrm={isCRM} onCancel={() => closeModal(modelName)}/>
                    }
                    {state.viewOptions.listOther &&
                        <PaymentList type='digital-wallet' title={text("form.paymentManagement.otherMethodsHeader")} clickHandler={paymentClicked} isCrm={isCRM}/>
                    }
                    {state.viewOptions.listAll &&
                        <PaymentList type='all' clickHandler={paymentClicked} recaptchaKey={recaptchaKey} isCrm={isCRM}/>
                    }
                    <StandardButton text={text("form.paymentManagement.addNewPaymentType")} onClick={showSelectMethodList}/>
                </>}
                {state.mode === 'select' &&
                    <AddPaymentList clickHandler={showAddPayment} mode={state.mode} isCrm={isCRM} />
                }
                {state.mode === 'express' &&
                    <AddExpressPaymentList brainTreeToken={brainTreeToken} digitalWalletEnv={runEnv} googlePayMerchantId={googlePayMerchant} modelName={modelName} onChange={onChange} onSubmit={onSubmit}/>
                }
                {/* The following will need to change to show either a credit card panel or a digital wallet panel */}
                {(state.mode === 'addCard' || state.mode === 'edit') &&
                    <CreditCardPanel markets={markets} recaptchaKey={recaptchaKey} mode={state.mode} cardMessage={citMitMessage} modelName={modelName} onChange={onChange} onSubmit={state.mode === 'edit' ? undefined : onSubmit } cartShippingAddress={cartShippingAddress} setSubscriptionPayment={setSubscriptionPayment} subscriptionCode={subscriptionCode}/>
                }
                {(state.mode === 'addAPayment') &&
                    <AddPaymentList clickHandler={showAddPayment} mode={state.mode} />
                }
                {(state.mode === 'crmedit' || state.mode === 'crmaddCard') &&
                    <CreditCardPanel markets={markets} recaptchaKey={recaptchaKey} mode={state.mode} cardMessage={citMitMessage} modelName={modelName} paymentLineID={paymentLineID} onChange={onChange} csrfToken={csrfToken} setSubscriptionPayment={setSubscriptionPayment} subscriptionCode={subscriptionCode}/>
                }
                {(state.mode === 'addDigitalWallet' || state.mode === 'paypal'|| state.mode === 'apple-pay' ||state.mode === 'google-pay')&&
                    <AddDigitalWalletPanel brainTreeToken={brainTreeToken} digitalWalletEnv={runEnv} googlePayMerchantId={googlePayMerchant} modelName={modelName} onChange={onChange} onSubmit={onSubmit} setSubscriptionPayment={setSubscriptionPayment} subscriptionCode={subscriptionCode}/>
                }
                {state.mode === 'editDigitalWallet' &&
                    <EditDigitalWalletPanel onChange={onChange} onSubmit={onSubmit}/>
                }
            </PaymentViewDispatchContext.Provider>
        </PaymentViewStateContext.Provider>
    );

};

export const usePaymentViewState = () => {
    const state = useContext(PaymentViewStateContext);
    if (!state) {
        throw new Error('usePaymentViewState must be used within a PaymentView');
    }
    return state;
};

export const usePaymentViewMutator = () => {
    const dispatch = useContext(PaymentViewDispatchContext);
  const { context } = usePaymentsState();
    if (!dispatch) {
        throw new Error('usePaymentViewMutator must be used within a PaymentView');
    }
    const { methodCount } = useContext(PaymentViewStateContext);
    const setPaymentView = (mode) => {
        window.scrollTo(0, 0);
        if (mode === 'select' && methodCount === 1) {
            mode = 'addCard';
        }
        dispatch({type: SET_VIEW_MODE, payload: mode});
    };
    const showPaymentList = () => {
        window.scrollTo(0, 0);
        dispatch({type: SET_VIEW_MODE, payload: 'list'});
    };

    const showPaymentSelect = () => {
        window.scrollTo(0, 0);
        dispatch({type: SET_VIEW_MODE, payload: 'select'});
    };

    const showEditPayment = (payment) => {
        window.scrollTo(0, 0);
        let mode = payment.method === 'digital-wallet' ? 'editDigitalWallet' : context === 'crm-subscription' ? 'crmedit': 'edit';
        dispatch({type: SET_VIEW_MODE, payload:mode});
    };
    return { setPaymentView, showPaymentList, showEditPayment, showPaymentSelect };
};
